import { api, request } from './client';
import type {
  Act,
  CaseCard,
  CaseDocument,
  CaseItem,
  DownloadUrl,
  I18nPayload,
  LoginResult,
  Me,
  Meta,
  Page,
  QualityItemIn,
  ReceivedHandover,
  ReceivedHandoverDetail,
  Step,
  StepIn,
  TripFull,
  TripRow,
  UploadTicket,
  Vehicle,
} from './types';

const enc = encodeURIComponent;

export interface UploadIn {
  document_type_key: string;
  file_name: string;
  mime: string;
  size: number;
}

/** Where a step (and its photos) belongs: a handover received from VIA DOMUM or an own case. */
export type StepTarget = { kind: 'handover'; id: string } | { kind: 'case'; id: string };

function base(t: StepTarget): string {
  return t.kind === 'handover' ? `/handovers/received/${enc(t.id)}` : `/cases/${enc(t.id)}`;
}

/** Typed calls used by the partner app. Only endpoints a partner tenant may use (docs/API.md, phase 3 E). */
export const partner = {
  // auth
  login: (body: { email: string; password: string; totp?: string; organization_id?: string }) =>
    // cookie:false — the app lives on its bearer token only; a browser cookie would be shared with the CRM
    // (the CRM would open as this driver, and «Sign out» there would end the driver's session mid-step).
    request<LoginResult>('/auth/login', { method: 'POST', body: { ...body, cookie: false }, skipAuthRedirect: true }),
  me: () => request<Me>('/auth/me', { skipAuthRedirect: true }),
  changePassword: (current_password: string, new_password: string) =>
    api.post<{ ok: boolean }>('/auth/password', { current_password, new_password }),
  /** Remember the chosen interface language on the account (follows the person to another phone). */
  saveLocale: (locale: string) => api.patch<unknown>('/me/profile', { locale }),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST', body: {}, skipAuthRedirect: true }),

  // i18n (public)
  i18n: (namespace = 'partner') => request<I18nPayload>(`/i18n/${enc(namespace)}`, { anonymous: true, skipAuthRedirect: true }),

  meta: () => api.get<Meta>('/meta'),

  // handovers received from the sponsor
  handovers: () => api.get<ReceivedHandover[]>('/handovers/received'),
  handover: (id: string) => api.get<ReceivedHandoverDetail>(`/handovers/received/${enc(id)}`),
  accept: (id: string) => api.post<ReceivedHandoverDetail>(`/handovers/received/${enc(id)}/accept`),
  decline: (id: string) => api.post<ReceivedHandoverDetail>(`/handovers/received/${enc(id)}/decline`),
  handoverDocUrl: (id: string, docId: string) =>
    api.get<DownloadUrl>(`/handovers/received/${enc(id)}/documents/${enc(docId)}/download-url`),
  acts: (id: string) => api.get<{ items: Act[] }>(`/handovers/received/${enc(id)}/acts`),
  actUrl: (id: string, docId: string) => api.get<DownloadUrl>(`/handovers/received/${enc(id)}/acts/${enc(docId)}/download-url`),

  // steps with photos (handover or own case)
  steps: (t: StepTarget) => api.get<{ items: Step[] }>(`${base(t)}/steps`),
  addStep: (t: StepTarget, body: StepIn) => api.post<Step>(`${base(t)}/steps`, body),
  uploadUrl: (t: StepTarget, body: UploadIn) => api.post<UploadTicket>(`${base(t)}/documents/upload-url`, body),
  completeDocument: (docId: string) => api.post<{ ok: boolean }>(`/documents/${enc(docId)}/complete`),
  documentUrl: (docId: string) => api.get<DownloadUrl>(`/documents/${enc(docId)}/download-url`),

  // own cases
  cases: (q?: { open?: boolean; limit?: number }) => api.get<Page<CaseItem>>('/cases', { limit: 100, ...q }),
  case: (id: string) => api.get<CaseCard>(`/cases/${enc(id)}`),
  createCase: (body: {
    service_type_key: string;
    title?: string;
    deceased?: { full_name: string; death_place?: string; death_country?: string };
    origin_country?: string;
    dest_country?: string;
    customer?: { name?: string; phone?: string };
  }) => api.post<CaseCard>('/cases', body),
  caseDocuments: (id: string) => api.get<CaseDocument[]>(`/cases/${enc(id)}/documents`),
  transition: (id: string, to: string) => api.post<{ state_key: string }>(`/cases/${enc(id)}/transition`, { to }),

  // trips and vehicles
  trips: (q?: { driver?: 'me'; status?: string; date_from?: string; date_to?: string }) => api.get<TripRow[]>('/trips', q),
  trip: (id: string) => api.get<TripFull>(`/trips/${enc(id)}`),
  createTrip: (body: { vehicle_id: string; driver_id?: string; handover_id?: string; stops: { place: string; country?: string; etd_plan?: string; eta_plan?: string }[] }) =>
    api.post<TripFull>('/trips', body),
  linkTrip: (tripId: string, handoverId: string) => api.patch<TripFull>(`/trips/${enc(tripId)}`, { handover_id: handoverId }),
  vehicles: () => api.get<Vehicle[]>('/vehicles'),

  // «Проблема / ідея» → Quality workspace of the sponsor
  qualityItem: (body: QualityItemIn) => api.post<{ id: string }>('/partner/quality-items', body),
};

export type PartnerApi = typeof partner;
